import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { logger } from 'APP/Lib/Datadog'

import type {
  CallAcceptedPayload,
  CallEndedPayload,
  IncomingCallRequestedPayload,
  IncomingFullScreenCallPayload,
  NotificationCallAcceptedPayload,
  NotificationCallEndedPayload,
  ShowOngoingCallNotificationPayload,
} from './types'
import type { RootState } from '../CreateStore'

interface NativeCallAndroidState {
  /**
   * The session ID associated with the Android notification currently displayed on the device (if any), otherwise null.
   * This notification may be:
   * - An "incoming call" notification, unmodified by the app;
   * - An "ongoing call" notification, created by the app when the user accepts a call (either in-app or from a notification).
   *
   * The meaning of this store value is specific to the Android video call lifecycle, and we cannot simply use
   * the value from the shared `VideoSession` slice.
   */
  sessionId: string | null
}

export const initialState: NativeCallAndroidState = {
  sessionId: null,
}

export const selectNativeCallAndroid = (state: RootState) => state.nativeCallAndroid

const { actions, reducer } = createSlice({
  name: 'nativecallandroid',
  initialState,
  reducers: {
    incomingFullScreenCall: (state, action: PayloadAction<IncomingFullScreenCallPayload>) => {
      const { sessionId } = action.payload

      logger.info('Updating NativeCallAndroidState with sessionId from fullscreen notification', {
        sessionId,
      })

      state.sessionId = sessionId
    },
    incomingCallRequested: (state, action: PayloadAction<IncomingCallRequestedPayload>) => {
      state.sessionId = action.payload.sessionId
    },
    callAccepted: (_state, _action: PayloadAction<CallAcceptedPayload>) => {},
    callEnded: (_state, _action: PayloadAction<CallEndedPayload>) => {},
    dismissVideoCall: () => initialState,
    notificationCallAccepted: (
      _state,
      _action: PayloadAction<NotificationCallAcceptedPayload>
    ) => {},
    notificationCallEnded: (_state, _action: PayloadAction<NotificationCallEndedPayload>) => {},
    showOngoingCallNotification: (
      state,
      action: PayloadAction<ShowOngoingCallNotificationPayload>
    ) => {
      const { sessionId } = action.payload
      state.sessionId = sessionId
    },
  },
})

export { actions as nativeCallAndroidActions }

export default reducer
